import type { AxiosInstance } from 'axios';

import { invertMap } from '@/sections/insights/queryBuilder/entities/common/invert';
import {
  packStringsForAPI,
  unpackStringsForUI,
} from '@/sections/insights/queryBuilder/entities/common/multiValueHelpers';
import {
  getNotFromSimpleFilter,
  getOperatorFromQueryFilter,
  getOperatorFromSimpleFilter,
  getValueFromQueryFilter,
  getValueFromSimpleFilter,
} from '@/sections/insights/queryBuilder/entities/common/translators';
import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';
import type { QueryFilter } from '@/types';
import { formatPrettyName } from '@/utils/mappings.util';

// Module-level maps (populated by fetch)
let vendorDisplayMap: Record<string, string> = {};
let vendorDisplayMapInverse: Record<string, string> = {};

// Export for testing
export const __setVendorMapsForTesting = (
  displayMap: Record<string, string>
) => {
  vendorDisplayMap = displayMap;
  vendorDisplayMapInverse = invertMap(displayMap);
};

/**
 * Fetch technology vendors from statistics endpoint
 * Uses the same statistics key as useGetTechnologyFilters hook
 */
async function fetchTechnologyVendors(
  axios: AxiosInstance,
  options?: { isGlobal?: boolean }
): Promise<string[]> {
  try {
    const response = await axios.get<{
      count: number;
      statistics: Array<{ value: string }>;
    }>('/my', {
      params: {
        label: 'statistic',
        key: '#statistic#technology#vendor#now#',
        ...(options?.isGlobal ? { allTenants: true } : {}),
      },
    });

    // Build display maps for raw API values → pretty names
    vendorDisplayMap = {};
    response.data.statistics.forEach(item => {
      const raw = item.value.replace(/\\(.)/g, '$1');
      vendorDisplayMap[raw] = formatPrettyName(raw);
    });
    vendorDisplayMapInverse = invertMap(vendorDisplayMap);

    return Object.values(vendorDisplayMap).sort((left, right) =>
      left.localeCompare(right)
    );
  } catch {
    return [];
  }
}

/**
 * Technology Vendor filter definition
 * Dropdown with dynamically fetched vendor values
 */
export const TechnologyVendor: SimpleFilterDefinition = {
  apiField: 'vendor',
  uiField: 'Vendor',
  inputType: 'dropdown',
  getAvailableValues: fetchTechnologyVendors,
  operators: ['is', 'is not', 'is any of', 'is none of'],

  translateForUI: (queryFilter: QueryFilter): SimpleFilter => {
    const operator = getOperatorFromQueryFilter(queryFilter);
    const unpacked = unpackStringsForUI(queryFilter.value, operator);

    // Map API codes to display names using helper
    const uiValue = getValueFromQueryFilter(vendorDisplayMap, {
      ...queryFilter,
      value: unpacked,
    });

    return {
      field: 'Vendor',
      operator,
      value: uiValue,
    };
  },

  translateForAPI: (simpleFilter: SimpleFilter): QueryFilter => {
    // Map display names to API codes using helper
    const mappedValue = getValueFromSimpleFilter(
      vendorDisplayMapInverse,
      simpleFilter
    );

    // Then pack for API
    const apiValue = packStringsForAPI(
      mappedValue as string | string[],
      simpleFilter.operator
    );

    return {
      field: 'vendor',
      operator: getOperatorFromSimpleFilter(simpleFilter),
      value: apiValue,
      not: getNotFromSimpleFilter(simpleFilter),
    };
  },
};
